/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback } from "react";

import { useClient } from "../ClientContext";
import { useRegisterGuestUser } from "../auth/useRegisterGuestUser";
import { widget } from "../widget";

interface UseRegisterPasswordlessUserType {
  privacyPolicyUrl?: string;
  registerPasswordlessUser: (displayName: string) => Promise<void>;
  recaptchaId?: string;
}

export function useRegisterPasswordlessUser(): UseRegisterPasswordlessUserType {
  const { setClient } = useClient();
  const { privacyPolicyUrl, recaptchaId, registerGuestUser, reset } =
    useRegisterGuestUser();

  const registerPasswordlessUser = useCallback(
    async (displayName: string) => {
      if (!setClient) {
        throw new Error("No client context");
      }
      if (widget) {
        throw new Error(
          "Registration was skipped: We should never try to register password-less user in embedded mode.",
        );
      }

      try {
        const [client, session] = await registerGuestUser(displayName);
        setClient(client, session);
      } catch (e) {
        reset();
        throw e;
      }
    },
    [registerGuestUser, reset, setClient],
  );

  return { privacyPolicyUrl, registerPasswordlessUser, recaptchaId };
}
